import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useTheme } from '../contexts/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Account Completion Component (after registration)
export const AccountCompletion = ({ user, onComplete }) => {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [sameAsShipping, setSameAsShipping] = useState(false);
  const [addresses, setAddresses] = useState({
    billing: {
      street: '',
      city: '',
      province: '',
      postal_code: '',
      country: 'CA'
    },
    shipping: {
      street: '',
      city: '',
      province: '',
      postal_code: '',
      country: 'CA'
    }
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleAddressChange = (type, field, value) => {
    setAddresses(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));

    // If "same as billing" is checked, update shipping when billing changes
    if (sameAsShipping && type === 'billing') {
      setAddresses(prev => ({
        ...prev,
        shipping: {
          ...prev.billing,
          [field]: value
        }
      }));
    }
  };

  const handleSameAsShipping = (checked) => {
    setSameAsShipping(checked);
    if (checked) {
      setAddresses(prev => ({
        ...prev,
        shipping: { ...prev.billing }
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Ensure we include credentials for authentication
      const response = await axios.put(`${API}/customers/profile`, {
        billing_address: addresses.billing,
        shipping_address: addresses.shipping,
        profile_completed: true
      }, {
        withCredentials: true  // Ensure cookies are sent
      });

      toast.success('Account setup completed successfully!');
      onComplete();
    } catch (error) {
      console.error('Failed to complete account:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication expired. Please log in again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save account information');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    toast('You can complete your profile later in account settings', {
      icon: 'ℹ️',
      duration: 3000,
    });
    onComplete();
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'} py-12 px-4`}>
      <div className="max-w-2xl mx-auto">
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-8`}>
          <div className="text-center mb-8">
            {settings?.company_settings?.logo_url && (
              <img 
                src={settings.company_settings.logo_url} 
                alt="RG-3D Logo" 
                className="w-16 h-16 mx-auto mb-4 object-contain"
              />
            )}
            <h2 className="text-3xl font-bold text-gray-800">Complete Your Account</h2>
            <p className="text-gray-600 mt-2">
              Welcome {user?.name}! Let's set up your addresses for faster checkout.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Billing Address */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Billing Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Street Address</label>
                  <input
                    type="text"
                    value={addresses.billing.street}
                    onChange={(e) => handleAddressChange('billing', 'street', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="123 Main Street, Apt 4B"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">City</label>
                  <input
                    type="text"
                    value={addresses.billing.city}
                    onChange={(e) => handleAddressChange('billing', 'city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Toronto"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Province</label>
                  <select
                    value={addresses.billing.province}
                    onChange={(e) => handleAddressChange('billing', 'province', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Province</option>
                    <option value="ON">Ontario</option>
                    <option value="BC">British Columbia</option>
                    <option value="AB">Alberta</option>
                    <option value="MB">Manitoba</option>
                    <option value="SK">Saskatchewan</option>
                    <option value="QC">Quebec</option>
                    <option value="NB">New Brunswick</option>
                    <option value="NS">Nova Scotia</option>
                    <option value="PE">Prince Edward Island</option>
                    <option value="NL">Newfoundland and Labrador</option>
                    <option value="YT">Yukon</option>
                    <option value="NT">Northwest Territories</option>
                    <option value="NU">Nunavut</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Postal Code</label>
                  <input
                    type="text"
                    value={addresses.billing.postal_code}
                    onChange={(e) => handleAddressChange('billing', 'postal_code', e.target.value.toUpperCase())}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="K1A 0A6"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Country</label>
                  <select
                    value={addresses.billing.country}
                    onChange={(e) => handleAddressChange('billing', 'country', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="CA">Canada</option>
                    <option value="US">United States</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-800">Shipping Address</h3>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="sameAsShipping"
                    checked={sameAsShipping}
                    onChange={(e) => handleSameAsShipping(e.target.checked)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="sameAsShipping" className="text-sm font-medium">
                    Same as billing address
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Street Address</label>
                  <input
                    type="text"
                    value={addresses.shipping.street}
                    onChange={(e) => handleAddressChange('shipping', 'street', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="123 Main Street, Apt 4B"
                    disabled={sameAsShipping}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">City</label>
                  <input
                    type="text"
                    value={addresses.shipping.city}
                    onChange={(e) => handleAddressChange('shipping', 'city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Toronto"
                    disabled={sameAsShipping}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Province</label>
                  <select
                    value={addresses.shipping.province}
                    onChange={(e) => handleAddressChange('shipping', 'province', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={sameAsShipping}
                  >
                    <option value="">Select Province</option>
                    <option value="ON">Ontario</option>
                    <option value="BC">British Columbia</option>
                    <option value="AB">Alberta</option>
                    <option value="MB">Manitoba</option>
                    <option value="SK">Saskatchewan</option>
                    <option value="QC">Quebec</option>
                    <option value="NB">New Brunswick</option>
                    <option value="NS">Nova Scotia</option>
                    <option value="PE">Prince Edward Island</option>
                    <option value="NL">Newfoundland and Labrador</option>
                    <option value="YT">Yukon</option>
                    <option value="NT">Northwest Territories</option>
                    <option value="NU">Nunavut</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Postal Code</label>
                  <input
                    type="text"
                    value={addresses.shipping.postal_code}
                    onChange={(e) => handleAddressChange('shipping', 'postal_code', e.target.value.toUpperCase())}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="K1A 0A6"
                    disabled={sameAsShipping}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Country</label>
                  <select
                    value={addresses.shipping.country}
                    onChange={(e) => handleAddressChange('shipping', 'country', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={sameAsShipping}
                  >
                    <option value="CA">Canada</option>
                    <option value="US">United States</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors duration-200 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Complete Account Setup'}
              </button>
              <button
                type="button"
                onClick={handleSkip}
                className="bg-gray-500 hover:bg-gray-600 text-white py-3 px-6 rounded-lg font-semibold transition-colors duration-200"
              >
                Skip for Now
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Balance Payment Component
const BalancePaymentModal = ({ order, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  
  const handlePayment = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/pay-balance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: order.balance_owed,
          method: paymentMethod
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Payment failed');
      }
      
      toast.success('Payment recorded successfully! New balance: $' + result.new_balance.toFixed(2));
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Payment failed:', error);
      toast.error('Payment failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">💳 Record Payment</h3>
        
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            ℹ️ This will record your payment intention. Please complete actual payment via your selected method.
          </p>
        </div>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Order #{order.id?.substring(0, 8)}</p>
          <p className="text-2xl font-bold text-orange-600">${order.balance_owed?.toFixed(2)}</p>
          <p className="text-sm text-gray-500">Balance Owed</p>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Payment Method</label>
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="paypal">PayPal</option>
            <option value="etransfer">E-Transfer</option>
            <option value="pickup">Pay on Pickup</option>
          </select>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handlePayment}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Processing...' : 'Pay Now'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Shipping Change Modal
const ShippingChangeModal = ({ order, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [shippingMethod, setShippingMethod] = useState('standard');
  
  const shippingCosts = {
    standard: 5.99,
    express: 15.99,
    overnight: 29.99
  };
  
  const handleChange = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/change-shipping`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          shipping_method: shippingMethod
        })
      });
      
      if (!response.ok) throw new Error('Failed to change shipping');
      
      const data = await response.json();
      alert(`Shipping changed! New balance: $${data.new_balance.toFixed(2)}`);
      onSuccess();
      onClose();
    } catch (error) {
      alert('Failed to change shipping: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">📦 Change Shipping Method</h3>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Current: Pickup (Free)
          </p>
          
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">New Shipping Method</label>
          <select
            value={shippingMethod}
            onChange={(e) => setShippingMethod(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="standard">Standard Shipping ($5.99)</option>
            <option value="express">Express Shipping ($15.99)</option>
            <option value="overnight">Overnight Shipping ($29.99)</option>
          </select>
          
          <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
            ⚠️ This will add ${shippingCosts[shippingMethod].toFixed(2)} to your balance owed
          </p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleChange}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Changing...' : 'Confirm Change'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Customer Account Dashboard
export const CustomerAccount = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingAddresses, setEditingAddresses] = useState(false);
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [settings, setSettings] = useState(null);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    billing_address: null,
    shipping_address: null,
    marketing_emails_enabled: true
  });
  const [addressData, setAddressData] = useState({
    billing_address: {
      street: '',
      city: '',
      province: '',
      postal_code: '',
      country: 'CA'
    },
    shipping_address: {
      street: '',
      city: '',
      province: '',
      postal_code: '',
      country: 'CA'
    }
  });

  useEffect(() => {
    loadCustomerData();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadCustomerData = async () => {
    try {
      const [profileResponse, ordersResponse] = await Promise.all([
        axios.get(`${API}/customers/profile`, { withCredentials: true }),
        axios.get(`${API}/customers/orders`, { withCredentials: true })
      ]);

      setUser(profileResponse.data);
      setProfileData(profileResponse.data);
      
      // Enrich orders with addon details
      const enrichedOrders = await Promise.all(
        ordersResponse.data.map(async (order) => {
          const enrichedItems = await Promise.all(
            (order.items || []).map(async (item) => {
              try {
                // Fetch product details to get addon names, prices, and base price
                const productResponse = await axios.get(`${API}/products/${item.product_id}`);
                const product = productResponse.data;
                
                // Store the actual product base price
                item.product_base_price = product.price || 0;
                
                // Transform selected_addons from IDs to full addon objects
                if (item.selected_addons && Array.isArray(item.selected_addons) && product.addons) {
                  item.selected_addons = item.selected_addons.map(addonId => {
                    const addon = product.addons.find(a => a.id === addonId);
                    if (addon) {
                      return {
                        id: addon.id,
                        name: addon.name || addon.custom_name || 'Add-on',
                        price: addon.price || 0
                      };
                    }
                    return {
                      id: addonId,
                      name: 'Unknown Add-on',
                      price: 0
                    };
                  });
                  
                  // Calculate addon_total from enriched addons
                  item.addon_total = item.selected_addons.reduce((sum, addon) => sum + (addon.price || 0), 0);
                }
                
                // Enrich selected_options with prices from product's base_options
                if (item.selected_options && product.base_options) {
                  let optionTotal = 0;
                  const enrichedOptions = {};
                  
                  // Handle both object and array formats
                  const selectedOpts = typeof item.selected_options === 'object' && !Array.isArray(item.selected_options)
                    ? item.selected_options
                    : {};
                  
                  Object.entries(selectedOpts).forEach(([optionKey, optionValue]) => {
                    const selectedVal = typeof optionValue === 'object' ? optionValue.value : optionValue;
                    
                    // Find this option in product's base_options
                    if (product.base_options[optionKey] && Array.isArray(product.base_options[optionKey])) {
                      const optionDef = product.base_options[optionKey].find(opt => opt.name === selectedVal);
                      if (optionDef) {
                        enrichedOptions[optionKey] = {
                          value: selectedVal,
                          price: optionDef.price || 0
                        };
                        optionTotal += optionDef.price || 0;
                      } else {
                        enrichedOptions[optionKey] = {
                          value: selectedVal,
                          price: 0
                        };
                      }
                    } else {
                      // Keep original if can't find in product
                      enrichedOptions[optionKey] = typeof optionValue === 'object' ? optionValue : { value: optionValue, price: 0 };
                    }
                  });
                  
                  item.selected_options = enrichedOptions;
                  item.option_total = optionTotal;
                }
                
                return item;
              } catch (error) {
                console.error('Failed to fetch product for addon enrichment:', error);
                return item;
              }
            })
          );
          
          return {
            ...order,
            items: enrichedItems
          };
        })
      );
      
      setOrders(enrichedOrders);
      
      // Initialize address data from user profile
      setAddressData({
        billing_address: profileResponse.data.billing_address || {
          street: '',
          city: '',
          province: '',
          postal_code: '',
          country: 'CA'
        },
        shipping_address: profileResponse.data.shipping_address || {
          street: '',
          city: '',
          province: '',
          postal_code: '',
          country: 'CA'
        }
      });
    } catch (error) {
      console.error('Failed to load customer data:', error);
      toast.error('Failed to load account information');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(`${API}/customers/profile`, profileData, { withCredentials: true });
      setUser(response.data);
      setEditingProfile(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAddresses = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        ...profileData,
        billing_address: addressData.billing_address,
        shipping_address: addressData.shipping_address
      };

      const response = await axios.put(`${API}/customers/profile`, updateData, { withCredentials: true });
      setUser(response.data);
      setProfileData(response.data);
      setEditingAddresses(false);
      toast.success('Addresses updated successfully!');
      
      // Update address data state with saved data
      setAddressData({
        billing_address: response.data.billing_address || addressData.billing_address,
        shipping_address: response.data.shipping_address || addressData.shipping_address
      });
    } catch (error) {
      console.error('Failed to update addresses:', error);
      toast.error('Failed to update addresses');
    } finally {
      setLoading(false);
    }
  };

  const handleAddressChange = (addressType, field, value) => {
    setAddressData(prev => ({
      ...prev,
      [addressType]: {
        ...prev[addressType],
        [field]: value
      }
    }));

    // If "same as billing" is checked, update shipping when billing changes
    if (sameAsBilling && addressType === 'billing_address') {
      setAddressData(prev => ({
        ...prev,
        shipping_address: {
          ...prev.billing_address,
          [field]: value
        }
      }));
    }
  };

  const handleSameAsBilling = (checked) => {
    setSameAsBilling(checked);
    if (checked) {
      setAddressData(prev => ({
        ...prev,
        shipping_address: { ...prev.billing_address }
      }));
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Account Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-gray-700 mb-2">Personal Information</h4>
            <div className="space-y-2 text-sm">
              <p><strong>Name:</strong> {user?.name}</p>
              <p><strong>Email:</strong> {user?.email}</p>
              <p><strong>Phone:</strong> {user?.phone || 'Not provided'}</p>
              <p><strong>Member since:</strong> {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}</p>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-gray-700 mb-2">Account Statistics</h4>
            <div className="space-y-2 text-sm">
              <p><strong>Total Orders:</strong> {orders.length}</p>
              <p><strong>Total Spent:</strong> ${orders.reduce((sum, order) => sum + order.total, 0).toFixed(2)}</p>
              <p><strong>Account Status:</strong> <span className="text-green-600">Active</span></p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Recent Orders</h3>
        {orders.length > 0 ? (
          <div className="space-y-4">
            {orders.slice(0, 3).map((order) => (
              <div key={order.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold">{order.order_number || `#${order.id.slice(-8)}`}</p>
                    <p className="text-sm text-gray-600">{new Date(order.created_at).toLocaleDateString()}</p>
                    <p className="text-sm text-gray-600">{order.items.length} item(s)</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">${order.total.toFixed(2)}</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      order.order_status === 'delivered' ? 'bg-green-100 text-green-800' :
                      order.order_status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                      order.order_status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {order.order_status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            <button
              onClick={() => setActiveTab('orders')}
              className="text-blue-600 hover:text-blue-700 font-medium text-sm"
            >
              View all orders →
            </button>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full mb-3">
              <span className="text-2xl">📦</span>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-3">No orders yet</p>
            <button
              onClick={() => navigate('/products')}
              className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
            >
              Start shopping →
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderOrders = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-xl font-semibold mb-4">Order History</h3>
      {orders.length > 0 ? (
        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.id} className="border rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="font-semibold text-lg">{order.order_number || `#${order.id.slice(-8)}`}</h4>
                  <p className="text-gray-600">Placed on {new Date(order.created_at).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-lg">${order.total.toFixed(2)}</p>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    order.order_status === 'delivered' ? 'bg-green-100 text-green-800' :
                    order.order_status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                    order.order_status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {order.order_status.charAt(0).toUpperCase() + order.order_status.slice(1)}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <h5 className="font-medium text-gray-700 mb-3">Items ({order.items.length})</h5>
                  <div className="space-y-4">
                    {order.items.map((item, index) => (
                      <div key={index} className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                        {/* Product Name and Itemized Pricing */}
                        <div className="mb-3">
                          <p className="font-medium text-lg text-gray-900 mb-3">
                            {item.product_name || item.name || 'Product'}
                          </p>
                          
                          {/* Itemized Price List */}
                          <div className="space-y-2 text-sm">
                            {/* Base Product */}
                            <div className="flex justify-between items-center">
                              <div className="flex-1">
                                <span className="text-gray-900">Product (Base Price)</span>
                              </div>
                              <span className="font-medium text-gray-900">${(item.product_base_price || item.price || 0).toFixed(2)}</span>
                            </div>
                            
                            {/* Options with prices */}
                            {item.selected_options && (Array.isArray(item.selected_options) ? item.selected_options : Object.entries(item.selected_options)).map((option, i) => {
                              let optionName = '';
                              let optionValue = '';
                              let optionPrice = 0;
                              
                              if (Array.isArray(item.selected_options)) {
                                optionName = option.name || option.key || 'Option';
                                optionValue = option.value;
                                optionPrice = option.price || 0;
                              } else {
                                optionName = option[0];
                                optionValue = typeof option[1] === 'object' ? option[1].value : option[1];
                                optionPrice = typeof option[1] === 'object' ? (option[1].price || 0) : 0;
                              }
                              
                              console.log('Customer Option Debug:', { optionName, optionValue, optionPrice, option, rawData: item.selected_options });
                              
                              // Always show options, even if price is 0, but indicate if there's a price
                              return (
                                <div key={i} className="flex justify-between items-center">
                                  <div className="flex-1">
                                    <span className="text-blue-600">+ {optionName}: {optionValue}</span>
                                  </div>
                                  <span className="font-medium text-blue-600">+${optionPrice.toFixed(2)}</span>
                                </div>
                              );
                            })}
                            
                            {/* Add-ons with names and prices */}
                            {item.selected_addons && item.selected_addons.map((addon, i) => {
                              const addonName = typeof addon === 'object' ? (addon.name || addon.custom_name || 'Add-on') : 'Add-on';
                              const addonPrice = typeof addon === 'object' ? (addon.price || 0) : 0;
                              
                              console.log('Customer Addon Debug:', { addonName, addonPrice, addon });
                              
                              return (
                                <div key={i} className="flex justify-between items-center">
                                  <div className="flex-1">
                                    <span className="text-green-600">+ Add-on: {addonName}</span>
                                  </div>
                                  <span className="font-medium text-green-600">+${addonPrice.toFixed(2)}</span>
                                </div>
                              );
                            })}
                            
                            {/* Custom text/personalization (if has price) */}
                            {item.custom_text_inputs && (Array.isArray(item.custom_text_inputs) ? item.custom_text_inputs : Object.entries(item.custom_text_inputs)).map((input, i) => {
                              let inputPrice = 0;
                              let inputName = '';
                              
                              if (Array.isArray(item.custom_text_inputs)) {
                                inputPrice = input.price || 0;
                                inputName = input.name || 'Personalization';
                              } else {
                                inputPrice = typeof input[1] === 'object' ? (input[1].price || 0) : 0;
                                inputName = input[0];
                              }
                              
                              console.log('Customer Personalization Debug:', { inputName, inputPrice, input });
                              
                              return (
                                <div key={i} className="flex justify-between items-center">
                                  <div className="flex-1">
                                    <span className="text-purple-600">+ {inputName}</span>
                                  </div>
                                  <span className="font-medium text-purple-600">+${inputPrice.toFixed(2)}</span>
                                </div>
                              );
                            })}
                            
                            {/* Subtotal per item */}
                            <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                              <span className="font-medium text-gray-900">Price per Item:</span>
                              <span className="font-bold text-gray-900">${((item.product_base_price || item.price) + (item.option_total || 0) + (item.addon_total || 0)).toFixed(2)}</span>
                            </div>
                            
                            {/* Quantity */}
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600">Quantity:</span>
                              <span className="font-medium text-gray-600">× {item.quantity || 1}</span>
                            </div>
                            
                            {/* Item Total */}
                            <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                              <span className="font-bold text-lg text-gray-900">Item Total:</span>
                              <span className="font-bold text-xl text-blue-600">
                                ${(((item.product_base_price || item.price) + (item.option_total || 0) + (item.addon_total || 0)) * (item.quantity || 1)).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Selected Options */}
                        {item.selected_options && (Array.isArray(item.selected_options) ? item.selected_options.length > 0 : Object.keys(item.selected_options).length > 0) && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-1">Options:</p>
                            <div className="flex flex-wrap gap-2">
                              {Array.isArray(item.selected_options) ? (
                                // Handle array format: [{name: "Size", value: "50MM", price: 0}]
                                item.selected_options.map((option, i) => (
                                  <span key={i} className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                                    {option.name || option.key || 'Option'}: {option.value}
                                  </span>
                                ))
                              ) : (
                                // Handle object format: {Size: "50MM"}
                                Object.entries(item.selected_options).map(([key, value], i) => (
                                  <span key={i} className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                                    {key}: {typeof value === 'object' ? (value.value || JSON.stringify(value)) : value}
                                  </span>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Custom Text Personalization */}
                        {item.custom_text_inputs && (Array.isArray(item.custom_text_inputs) ? item.custom_text_inputs.length > 0 : Object.keys(item.custom_text_inputs).length > 0) && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-1">Personalization:</p>
                            <div className="space-y-1">
                              {Array.isArray(item.custom_text_inputs) ? (
                                // Handle array format: [{name: "Line 1", value: "John Doe"}]
                                item.custom_text_inputs.map((input, i) => (
                                  <p key={i} className="text-sm text-gray-700 bg-purple-50 px-3 py-2 rounded">
                                    <strong>{input.name || input.key || 'Text'}:</strong> {input.value}
                                  </p>
                                ))
                              ) : (
                                // Handle object format: {"Line 1": "John Doe"}
                                Object.entries(item.custom_text_inputs).map(([key, value], i) => (
                                  <p key={i} className="text-sm text-gray-700 bg-purple-50 px-3 py-2 rounded">
                                    <strong>{key}:</strong> {typeof value === 'object' ? (value.value || JSON.stringify(value)) : value}
                                  </p>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Selected Add-ons */}
                        {item.selected_addons && item.selected_addons.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-1">Add-ons:</p>
                            <div className="space-y-2">
                              {item.selected_addons.map((addonIdOrObj, i) => {
                                let addonName = 'Unknown Add-on';
                                let addonPrice = 0;
                                
                                if (typeof addonIdOrObj === 'string') {
                                  addonName = `Add-on ${i + 1}`;
                                } else if (addonIdOrObj && typeof addonIdOrObj === 'object') {
                                  addonName = addonIdOrObj.name || addonIdOrObj.custom_name || 'Add-on';
                                  addonPrice = addonIdOrObj.price || 0;
                                }
                                
                                return (
                                  <div key={i} className="inline-block bg-green-100 text-green-800 px-3 py-2 rounded-full text-sm">
                                    <span className="font-medium">{addonName}</span>
                                    {addonPrice > 0 && (
                                      <span className="ml-2 text-green-700">(+${addonPrice.toFixed(2)})</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Customization Notes */}
                        {item.customization_notes && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-1">Customization Notes:</p>
                            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">{item.customization_notes}</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Customer Uploaded Files */}
                        {item.uploaded_files && item.uploaded_files.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">📎 Your Uploaded Files:</p>
                            <div className="grid grid-cols-1 gap-2">
                              {item.uploaded_files.map((file, fileIndex) => {
                                const isImage = file.content_type?.startsWith('image/');
                                const fileUrl = file.file_url || `/api/files/${file.filename}`;
                                const displayName = file.original_filename || file.filename || `File ${fileIndex + 1}`;
                                
                                return (
                                  <div key={fileIndex} className="flex items-center justify-between p-2 rounded bg-blue-50 border border-blue-200">
                                    <div className="flex items-center gap-3 flex-1">
                                      {isImage && file.thumbnail_url ? (
                                        <img 
                                          src={`${process.env.REACT_APP_BACKEND_URL}${file.thumbnail_url}`}
                                          alt={displayName}
                                          className="w-12 h-12 object-cover rounded border"
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                      ) : (
                                        <div className="w-12 h-12 flex items-center justify-center rounded bg-white border">
                                          <span className="text-2xl">{isImage ? '🖼️' : '📄'}</span>
                                        </div>
                                      )}
                                      
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate" title={displayName}>
                                          {displayName}
                                        </p>
                                        {file.file_size && (
                                          <p className="text-xs text-gray-600">
                                            {(file.file_size / 1024).toFixed(1)} KB
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className="flex gap-2">
                                      {isImage && (
                                        <a
                                          href={`${process.env.REACT_APP_BACKEND_URL}${fileUrl}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="px-3 py-1 text-xs rounded bg-blue-500 text-white hover:bg-blue-600 transition"
                                          title="View image"
                                        >
                                          👁️ View
                                        </a>
                                      )}
                                      <a
                                        href={`${process.env.REACT_APP_BACKEND_URL}${fileUrl}`}
                                        download={displayName}
                                        className="px-3 py-1 text-xs rounded bg-green-500 text-white hover:bg-green-600 transition"
                                        title="Download file"
                                      >
                                        ⬇️ Download
                                      </a>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p><strong>Payment:</strong> {order.payment_method?.toUpperCase()}</p>
                      <p><strong>Delivery:</strong> {order.shipping_method === 'pickup' ? 'Local Pickup' : 'Shipping'}</p>
                    </div>
                    <div>
                      <p><strong>Subtotal:</strong> ${order.subtotal?.toFixed(2)}</p>
                      <p><strong>Tax:</strong> ${order.tax?.toFixed(2)}</p>
                      <p><strong>Total:</strong> ${order.total?.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {/* Balance Owed Section */}
                {order.balance_owed > 0 && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500 p-4 rounded mt-3">
                    <div className="flex justify-between items-center mb-3">
                      <div>
                        <p className="font-semibold text-orange-700 dark:text-orange-300">Balance Owed</p>
                        <p className="text-2xl font-bold text-orange-600">${order.balance_owed.toFixed(2)}</p>
                        {order.deposit_amount > 0 && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Deposit paid: ${order.deposit_amount.toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Order Information & Support Contact - Always Show */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-5 mt-3 shadow-sm">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
                          <span className="text-lg">📧</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-base text-blue-900 dark:text-blue-100 font-semibold mb-2">
                            Need to make changes or pay for your order?
                          </p>
                          <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                            Please contact customer support for:
                          </p>
                        </div>
                      </div>
                      
                      <ul className="text-sm text-blue-700 dark:text-blue-300 ml-11 mb-4 space-y-1">
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Payment arrangements and balance inquiries</span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Order modifications or cancellations</span>
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>Shipping and delivery updates</span>
                        </li>
                      </ul>
                      
                      
                      <div className="ml-11 pt-3 border-t border-blue-200 dark:border-blue-700">
                        <p className="text-xs text-blue-800 dark:text-blue-200 font-medium mb-2">
                          📋 Order Processing Information:
                        </p>
                        <div className="space-y-2 text-xs text-blue-700 dark:text-blue-300">
                          <p>
                            <span className="font-medium">⚠️ Important:</span> Orders will not be processed until balance is paid in full.
                          </p>
                          
                          {order.deposit_amount > 0 && order.shipping_method === 'pickup' ? (
                            <p>
                              <span className="font-medium">💰 Deposit Paid | 📦 Pickup:</span> You will be contacted once your order is completed. Balance must be paid prior to or during pickup.
                            </p>
                          ) : order.deposit_amount > 0 && order.shipping_method !== 'pickup' ? (
                            <>
                              <p>
                                <span className="font-medium">💰 Deposit Paid:</span> You will be contacted once your order is completed.
                              </p>
                              <p>
                                <span className="font-medium">🚚 Shipping:</span> A final invoice will be sent for payment before your order ships.
                              </p>
                            </>
                          ) : order.shipping_method === 'pickup' ? (
                            <p>
                              <span className="font-medium">📦 Pickup:</span> You will be contacted when your order is ready for pickup. Payment must be made at pickup.
                            </p>
                          ) : (
                            <p>
                              <span className="font-medium">🚚 Shipping:</span> A final invoice will be sent for payment before your order ships.
                            </p>
                          )}
                        </div>
                        
                        {/* Additional note for deposit + pickup orders */}
                        <div className="mt-3 p-3 bg-blue-100 dark:bg-blue-800/30 rounded-lg border border-blue-300 dark:border-blue-700">
                          <p className="text-xs text-blue-800 dark:text-blue-200">
                            <span className="font-semibold">💡 Note:</span> If you've made a deposit, you will be contacted once your order is completed. The remaining balance must be paid prior to or during pickup.
                          </p>
                        </div>
                      </div>
                    </div>

                {order.payment_status === 'paid' && (
                  <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-3 rounded mt-3">
                    <p className="text-sm text-green-700 dark:text-green-300">
                      ✅ Paid in Full - Order being processed
                    </p>
                  </div>
                )}

                {order.tracking_number && (
                  <div className="bg-blue-50 p-3 rounded">
                    <p className="text-sm"><strong>Tracking Number:</strong> {order.tracking_number}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
            <span className="text-3xl">📦</span>
          </div>
          <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">No Orders Yet</h4>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            You haven't placed any orders. Start shopping to see your order history here.
          </p>
          <button
            onClick={() => navigate('/products')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-medium transition-colors"
          >
            Browse Products
          </button>
        </div>
      )}
    </div>
  );

  const renderProfile = () => {
    return (
      <div className="space-y-6">
        {/* Personal Information Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold">Personal Information</h3>
            <button
              onClick={() => setEditingProfile(!editingProfile)}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              {editingProfile ? 'Cancel' : 'Edit Profile'}
            </button>
          </div>

          {editingProfile ? (
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name</label>
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email Address</label>
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Phone Number (Optional)</label>
                <input
                  type="tel"
                  value={profileData.phone || ''}
                  onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingProfile(false)}
                  className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600">Full Name</p>
                  <p className="font-medium">{user?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email Address</p>
                  <p className="font-medium">{user?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone Number</p>
                  <p className="font-medium">{user?.phone || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Member Since</p>
                  <p className="font-medium">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Address Management Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold">Address Management</h3>
            <button
              onClick={() => setEditingAddresses(!editingAddresses)}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              {editingAddresses ? 'Cancel' : 'Edit Addresses'}
            </button>
          </div>

          {editingAddresses ? (
            <form onSubmit={handleUpdateAddresses} className="space-y-6">
              {/* Billing Address */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold mb-4 text-blue-800">💳 Billing Address</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Street Address</label>
                    <input
                      type="text"
                      value={addressData.billing_address.street}
                      onChange={(e) => handleAddressChange('billing_address', 'street', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="123 Main Street, Apt 4B"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">City</label>
                    <input
                      type="text"
                      value={addressData.billing_address.city}
                      onChange={(e) => handleAddressChange('billing_address', 'city', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Toronto"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Province/State</label>
                    <input
                      type="text"
                      value={addressData.billing_address.province}
                      onChange={(e) => handleAddressChange('billing_address', 'province', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ontario"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Postal Code</label>
                    <input
                      type="text"
                      value={addressData.billing_address.postal_code}
                      onChange={(e) => handleAddressChange('billing_address', 'postal_code', e.target.value.toUpperCase())}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="K1A 0A6"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Country</label>
                    <select
                      value={addressData.billing_address.country}
                      onChange={(e) => handleAddressChange('billing_address', 'country', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="CA">Canada</option>
                      <option value="US">United States</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold text-green-800">📦 Shipping Address</h4>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="sameAsBilling"
                      checked={sameAsBilling}
                      onChange={(e) => handleSameAsBilling(e.target.checked)}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="sameAsBilling" className="text-sm font-medium">
                      Same as billing address
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Street Address</label>
                    <input
                      type="text"
                      value={addressData.shipping_address.street}
                      onChange={(e) => handleAddressChange('shipping_address', 'street', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="123 Main Street, Apt 4B"
                      disabled={sameAsBilling}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">City</label>
                    <input
                      type="text"
                      value={addressData.shipping_address.city}
                      onChange={(e) => handleAddressChange('shipping_address', 'city', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Toronto"
                      disabled={sameAsBilling}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Province/State</label>
                    <input
                      type="text"
                      value={addressData.shipping_address.province}
                      onChange={(e) => handleAddressChange('shipping_address', 'province', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ontario"
                      disabled={sameAsBilling}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Postal Code</label>
                    <input
                      type="text"
                      value={addressData.shipping_address.postal_code}
                      onChange={(e) => handleAddressChange('shipping_address', 'postal_code', e.target.value.toUpperCase())}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="K1A 0A6"
                      disabled={sameAsBilling}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Country</label>
                    <select
                      value={addressData.shipping_address.country}
                      onChange={(e) => handleAddressChange('shipping_address', 'country', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={sameAsBilling}
                    >
                      <option value="CA">Canada</option>
                      <option value="US">United States</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Addresses'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingAddresses(false)}
                  className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              {/* Personal Information Display */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3">Personal Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600">Full Name</p>
                    <p className="font-medium">{user?.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email Address</p>
                    <p className="font-medium">{user?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone Number</p>
                    <p className="font-medium">{user?.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Member Since</p>
                    <p className="font-medium">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}</p>
                  </div>
                </div>
              </div>

              {/* Address Display */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3">Saved Addresses</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Billing Address Display */}
                  <div className="border border-blue-200 bg-blue-50 p-4 rounded-lg">
                    <h5 className="font-semibold text-blue-800 mb-2">💳 Billing Address</h5>
                    {user?.billing_address ? (
                      <div className="text-sm space-y-1">
                        <p className="font-medium">{user.billing_address.street}</p>
                        <p>{user.billing_address.city}, {user.billing_address.province} {user.billing_address.postal_code}</p>
                        <p>{user.billing_address.country}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No billing address saved</p>
                    )}
                  </div>

                  {/* Shipping Address Display */}
                  <div className="border border-green-200 bg-green-50 p-4 rounded-lg">
                    <h5 className="font-semibold text-green-800 mb-2">📦 Shipping Address</h5>
                    {user?.shipping_address ? (
                      <div className="text-sm space-y-1">
                        <p className="font-medium">{user.shipping_address.street}</p>
                        <p>{user.shipping_address.city}, {user.shipping_address.province} {user.shipping_address.postal_code}</p>
                        <p>{user.shipping_address.country}</p>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No shipping address saved</p>
                    )}
                  </div>
                </div>
                
                {/* Quick Address Setup */}
                {(!user?.billing_address || !user?.shipping_address) && (
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.348 15.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="font-semibold text-yellow-800">Address Setup Recommended</span>
                    </div>
                    <p className="text-sm text-yellow-700 mb-3">
                      Add your addresses for faster checkout and automatic shipping quotes.
                    </p>
                    <button
                      onClick={() => setEditingAddresses(true)}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded transition-colors"
                    >
                      Add Addresses Now
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    // Validate password length
    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    try {
      setPasswordLoading(true);
      const token = localStorage.getItem('access_token');
      
      await axios.put(`${API}/auth/customer/password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password updated successfully!');
      setChangingPassword(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Failed to update password:', error);
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const renderSettings = () => {
    return (
      <div className="space-y-6">
        {/* Change Password Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold">Change Password</h3>
              <p className="text-sm text-gray-600">Update your account password</p>
            </div>
            {!changingPassword && (
              <button
                onClick={() => setChangingPassword(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Change Password
              </button>
            )}
          </div>
          
          {changingPassword && (
            <form onSubmit={handlePasswordChange} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password *
                </label>
                <input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your current password"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password *
                  </label>
                  <input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    required
                    minLength="6"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Minimum 6 characters"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password *
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    required
                    minLength="6"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Re-enter new password"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {passwordLoading ? 'Updating...' : 'Update Password'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setChangingPassword(false);
                    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                <p className="text-sm text-blue-800">
                  <strong>💡 Tip:</strong> Use a strong password with at least 6 characters including letters, numbers, and symbols.
                </p>
              </div>
            </form>
          )}
        </div>
        
        {/* Account Settings Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Notification Preferences</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b">
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-gray-600">Receive updates about your orders</p>
              </div>
              <input
                type="checkbox"
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                defaultChecked={true}
              />
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <div>
                <p className="font-medium">Marketing Emails</p>
                <p className="text-sm text-gray-600">Receive promotions and new product announcements</p>
              </div>
              <input
                type="checkbox"
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                checked={profileData.marketing_emails_enabled !== false}
                onChange={async (e) => {
                  const newValue = e.target.checked;
                  setProfileData({...profileData, marketing_emails_enabled: newValue});
                  try {
                    await axios.put(`${API}/customers/profile`, 
                      { marketing_emails_enabled: newValue }, 
                      { withCredentials: true }
                    );
                    toast.success(newValue ? 'Marketing emails enabled' : 'Marketing emails disabled');
                  } catch (error) {
                    console.error('Failed to update marketing preference:', error);
                    toast.error('Failed to update preference');
                    // Revert on error
                    setProfileData({...profileData, marketing_emails_enabled: !newValue});
                  }
                }}
              />
            </div>
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium">SMS Notifications</p>
                <p className="text-sm text-gray-600">Receive text updates about order status</p>
              </div>
              <input
                type="checkbox"
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                defaultChecked={false}
              />
            </div>
            
            <div className="pt-4">
              <button className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700">
                Delete Account
              </button>
              <p className="text-xs text-gray-500 mt-1">This action cannot be undone</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">My Account</h1>
          <p className="text-gray-600">Manage your account information and view your orders</p>
        </div>

        {/* Account Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-8 border-b">
            {[
              { id: 'overview', label: 'Overview', icon: '👤' },
              { id: 'orders', label: 'Orders', icon: '📦' },
              { id: 'profile', label: 'Profile & Addresses', icon: '✏️' },
              { id: 'settings', label: 'Settings', icon: '⚙️' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'orders' && renderOrders()}
        {activeTab === 'profile' && renderProfile()}
        {activeTab === 'settings' && renderSettings()}
      </div>

      {/* Payment and Shipping Modals removed - customers should contact support for order changes */}
    </div>
  );
};

export default CustomerAccount;